import { useState } from "react";
import { useCycle } from "../../context/CycleContext";
import { getMyKraKpi } from "../../services/kraKpiApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Loader, ErrorState, Button } from "../../components/common";
import { FiPlus } from "react-icons/fi";
import KpiMeasurements from "../../components/performance/KpiMeasurements";
import { KraTable, KpiTable } from "../../components/performance/KraKpiTable";
import { calcWeightedItems } from "../../utils/scoreCalculator";
import { formatScore } from "../../utils/format";
import MyProposalsCard from "../../components/proposals/MyProposalsCard";
import KraKpiTreeCard from "../../components/proposals/KraKpiTreeCard";
import KraGoalExplainer from "../../components/goals/KraGoalExplainer";
import GoalProgressModal from "../../components/goals/GoalProgressModal";
import { toast } from "react-toastify";
import { updateGoalProgress, getGoalProgressHistory } from "../../services/goalApi";
import { listProgressApprovals, isPendingApproval } from "../../services/progressApprovalApi";
import { Banner } from "../../components/common";
import { getErrorMessage } from "../../utils/errors";

const api = { getMyKraKpi };

export default function MyKraKpiPage() {
  const { cycleId, cycle } = useCycle();
  const { data, error, reload } = useAsync(() => api.getMyKraKpi(cycleId), [cycleId], { skip: !cycleId });
  const [treeVersion, setTreeVersion] = useState(0);
  const [addSignal, setAddSignal] = useState(0); // top-right button asks the proposals card to open its Add form
  // [NEW] The employee updates progress on a KRA or KPI; the manager approves it (Progress approvals).
  const pending = useAsync(() => listProgressApprovals({ view: "mine", status: "PENDING", recordId: data?.recordId }), [data?.recordId], { skip: !data?.recordId });
  const [progressItem, setProgressItem] = useState(null);
  const pendingIds = (pending.data || []).map((x) => String(x.itemId));
  const openProgress = (row) => setProgressItem({
    id: row.id, title: row.name, target: row.target, unit: row.unit,
    achievement: row.raw?.progress_pct ?? null, status: row.raw?.item_status,
  });
  const saveProgress = async (payload) => {
    try {
      const { data: saved } = await updateGoalProgress(progressItem.id, payload);
      toast.success(isPendingApproval(saved) ? "Progress sent to your manager for approval. It counts once it is approved." : "Progress saved.");
      pending.reload();
      reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const kraScore = calcWeightedItems(data.kras);

  return (
    <div className="page">
      <PageHeader
        title="My KRA & KPI"
        subtitle={`Your KRAs are your daily tasks. You add your own KRAs and KPIs for ${cycle?.name}; your manager approves them. Enter actual values during your self assessment.`}
        actions={<Button icon={FiPlus} onClick={() => setAddSignal((n) => n + 1)}>Add KRA / KPI</Button>}
      />
      <KraGoalExplainer view="employee" />
      {(pending.data || []).length > 0 && (
        <Banner tone="info" title={`${pending.data.length} progress update${pending.data.length === 1 ? "" : "s"} waiting for your manager`}>
          {pending.data.map((x) => `${x.itemTitle} (${x.progress ?? "—"}%)`).join(", ")}. They count once your manager approves; if your manager returns one, update it again.
        </Banner>
      )}
      <Card
        title="KRAs (my daily tasks)"
        subtitle={`${data.kras.length} added, ${data.kraWeightage}% goal percentage`}
        actions={<span className="score-chip">Score {formatScore(kraScore)}</span>}
        padded={false}
      >
        <KraTable rows={data.kras} onUpdate={data.trackingOpen ? openProgress : undefined} pending={pendingIds} />
      </Card>
      <Card
        title="Key performance indicators"
        subtitle={`${data.kpis.length} added; the KPIs of each KRA share it equally`}
        padded={false}
      >
        <KpiTable rows={data.kpis} onUpdate={data.trackingOpen ? openProgress : undefined} pending={pendingIds} />
      </Card>
      <KraKpiTreeCard cycleId={cycleId} version={treeVersion} />
      <MyProposalsCard
        recordId={data.recordId}
        sheetOpen={data.stage === "GOAL_SETTING"}
        addSignal={addSignal}
        cycleName={cycle?.name}
        dueDefault={cycle?.endDate}
        onChanged={() => { reload(); setTreeVersion((v) => v + 1); }}
      />
      {data.kpis.length > 0 && (
        <>
          <h2 className="section-title">KPI measurements</h2>
          {data.kpis.map((kpi) => <KpiMeasurements key={kpi.id} kpi={kpi} canRecord={data.trackingOpen} />)}
        </>
      )}
      <GoalProgressModal open={Boolean(progressItem)} goal={progressItem} onClose={() => setProgressItem(null)} onSave={saveProgress} loadHistory={getGoalProgressHistory} />
      <p className="footnote">
        Recorded measurements are visible to your manager and HR. Enter the final KPI actual in your self assessment ("Use" copies the latest recorded value).
        Scores are calculated by the system. For "lower is better" KPIs, coming in under target earns full marks.
      </p>
    </div>
  );
}